export function isObject(val: unknown): val is { [prop: PropertyKey]: unknown } {
  return val !== null && typeof val === 'object' && !Array.isArray(val);
}

export function omitObjectProp<T, Key extends keyof T>(obj: T, omitKey: Key): Omit<T, Key> | null {
  if (isObject(obj)) {
    return Object.keys(obj)
      .filter((key) => key !== omitKey)
      .reduce(
        (res, key) => {
          // @ts-ignore
          res[key] = obj[key];
          return res;
        },
        {} as Omit<T, Key>,
      );
  }
  return null;
}

export function isDeepEqual<T>(object1: T, object2: T): boolean {
  if (object1 === null && object2 === null) {
    return true;
  }
  if (typeof object1 === 'undefined' && typeof object2 === 'undefined') {
    return true;
  }

  const objKeys1 = isObject(object1) && Object.keys(object1);
  const objKeys2 = isObject(object2) && Object.keys(object2);

  if (!(objKeys1 && objKeys2)) {
    return false;
  }

  if (objKeys1.length !== objKeys2.length) {
    return false;
  }

  for (const key of objKeys1) {
    const value1 = object1[key];
    const value2 = object2[key];

    const isObjects = isObject(value1) && isObject(value2);

    if ((isObjects && !isDeepEqual(value1, value2)) || (!isObjects && value1 !== value2)) {
      return false;
    }
  }
  return true;
}

/**
 * Type definition for swapping keys and values of an object.
 *
 * This utility type takes an object `T` where both keys and values are strings,
 * and creates a new object type where the keys and values are swapped.
 *
 * @template T - The input object type (must extend `Record<string, string>`).
 * @example
 * type Original = { foo: "bar"; baz: "qux" };
 * type Swapped = SwapKeysAndValues<Original>;
 * // Swapped = { bar: "foo"; qux: "baz" }
 */
type SwapKeysAndValues<T extends Record<string, string>> = {
  [K in keyof T as T[K]]: K;
};

/**
 * Swaps the keys and values of a given object.
 *
 * This function takes an object where both keys and values are strings and returns
 * a new object with the keys and values swapped. The function is type-safe and ensures
 * that the returned object adheres to the `SwapKeysAndValues` type.
 *
 * @template T - The input object type (must extend `Record<string, string>`).
 * @param obj - The input object whose keys and values need to be swapped.
 * @returns A new object with keys and values swapped.
 *
 * @example
 * const input = { foo: "bar", baz: "qux" };
 * const result = swapKeysAndValues(input);
 * // result = { bar: "foo", qux: "baz" }
 * // Type of result = { bar: "foo"; qux: "baz"; }
 */
export function swapKeysAndValues<T extends Record<string, string>>(obj: T): SwapKeysAndValues<T> {
  const swapped: Record<string, string> = {};
  for (const [key, value] of Object.entries(obj)) {
    swapped[value] = key;
  }
  return swapped as SwapKeysAndValues<T>;
}
